import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Avatar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Preview as PreviewIcon,
  Code as CodeIcon,
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import ReactMarkdown from 'react-markdown';
import WorkflowChat from './WorkflowChat';
import GrootWorkflowChat from './GrootWorkflowChat';
import useDocuments from '../../hooks/useDocuments';
import useNotification from '../../hooks/useNotification';
import grootImageUrl from '../../assets/images/groot.jpg';

/**
 * ContentEditor component for PRD workflow
 * Allows users to:
 * 1. Edit markdown content directly
 * 2. Preview the markdown render
 * 3. Use AI chat to generate content
 * 4. Save changes and create documents automatically
 */
// Custom styled Groot avatar
const GrootAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: '#4d9559',
  border: `1px solid ${theme.palette.common.white}`,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
}));

const ContentEditor = ({
  title,
  content,
  onSave,
  sectionName,
  projectId,
  generateLabel = "Generate Content",
  placeholder = "Enter markdown content..."
}) => {
  const [editContent, setEditContent] = useState((content === null || content === undefined) ? '' : content);
  const [editMode, setEditMode] = useState(!content); // Start in edit mode if no content
  const [selectedTab, setSelectedTab] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDocumentSaved, setIsDocumentSaved] = useState(false);
  const [useGrootMode, setUseGrootMode] = useState(true); // Default to using Groot

  // Get document service and notification hooks
  const { 
    generateFieldValidations,
    generateTechStack,
    generateDevGuidelines,
    generateDesignGuidelines,
    generateSecurityGuidelines,
    generateAPIGuidelines,
    generateFeatures,
    generateUserStories,
    generateFinalPRD,
    createDocument,
    saveMdFile,
    getProjectDocuments
  } = useDocuments();
  const { showNotification } = useNotification();

  // State for related section content
  const [relatedSections, setRelatedSections] = useState({});
  
  // Memoized function for fetching related section content
  const fetchRelatedContent = useCallback(async () => {
    if (!projectId) return;
    
    console.log('Fetching related content for project:', projectId);
    
    try {
      // Get all documents for the project
      const documents = await getProjectDocuments(projectId);
      
      if (!documents || documents.length === 0) {
        console.log('No documents found for project');
        return;
      }
      
      console.log(`Found ${documents.length} documents for project`);
      
      // Temporary storage for section content
      const sectionContent = {};
      
      // Map document types to section names
      const documentTypeToSection = {
        'VALIDATION': 'Field Validations',
        'FIELD_VALIDATION': 'Field Validations',
        'TECH_STACK': 'Technology Stack',
        'DEV_GUIDELINES': 'Development Guidelines',
        'DESIGN_GUIDELINES': 'Design Guidelines',
        'SECURITY_GUIDELINES': 'Security Guidelines',
        'API_GUIDELINES': 'API Guidelines',
        'FEATURES': 'Features',
        'USER_STORIES': 'User Stories',
        'FINAL_PRD': 'Final PRD'
      };
      
      // Extract content for each section
      documents.forEach(doc => {
        const docSectionName = doc.title?.split(' for ')[0] || 
                           documentTypeToSection[doc.documentType] ||
                           doc.type;
                           
        if (docSectionName && doc.content) {
          sectionContent[docSectionName] = doc.content;
        }
      });
      
      // Update state with found section content
      setRelatedSections(sectionContent);
    } catch (error) {
      console.error('Error fetching related section content:', error);
    }
  }, [projectId, getProjectDocuments]);
  
  // Use a ref to prevent duplicate fetches on renders
  const hasFetchedRef = useRef({});
  
  // Effect to trigger fetch once when component mounts or projectId changes 
  useEffect(() => {
    // Skip if no projectId or already fetched for this project+section
    if (!projectId) return;
    
    const fetchKey = `${projectId}-${sectionName}`;
    
    // Only fetch if we haven't fetched for this specific combination yet
    if (!hasFetchedRef.current[fetchKey]) {
      console.log(`First fetch for ${fetchKey}`);
      
      // Mark as fetched before the actual fetch to prevent race conditions
      hasFetchedRef.current[fetchKey] = true;
      
      // Execute the fetch
      fetchRelatedContent();
    } else {
      console.log(`Already fetched for ${fetchKey}`);
    }
    
    // Cleanup function
    return () => {
      // No cleanup needed as we use the ref
    };
  }, [projectId, sectionName, fetchRelatedContent]);
  
  // Update content and reset state when section changes
  useEffect(() => {
    // Reset to first tab when changing sections
    setSelectedTab(0);
    
    // Reset edit mode - editable if no content
    setEditMode(!content);
    
    // Reset document saved status
    setIsDocumentSaved(false);
    
    // Update content with null/undefined safety
    if (content !== null && content !== undefined) {
      setEditContent(content);
    } else {
      // Clear content when moving to a different section with no content
      setEditContent('');
    }
  }, [content, sectionName]);
  

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Generate content for current section
  const handleGenerateContent = async () => {
    setIsGenerating(true);
    
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      
      // Determine which generator to use based on section name
      let generatorFn;
      let documentType;
      
      switch (sectionName) {
        case 'Field Validations':
          generatorFn = generateFieldValidations;
          documentType = 'validation';
          break;
        case 'Technology Stack':
          generatorFn = generateTechStack;
          documentType = 'techStack';
          break;
        case 'Development Guidelines':
          generatorFn = generateDevGuidelines;
          documentType = 'devGuidelines';
          break;
        case 'Design Guidelines':
          generatorFn = generateDesignGuidelines;
          documentType = 'designGuidelines';
          break;
        case 'Security Guidelines':
          generatorFn = generateSecurityGuidelines;
          documentType = 'securityGuidelines';
          break;
        case 'API Guidelines':
          generatorFn = generateAPIGuidelines;
          documentType = 'apiGuidelines';
          break;
        case 'Features':
          generatorFn = generateFeatures;
          documentType = 'features';
          break;
        case 'User Stories':
          generatorFn = generateUserStories;
          documentType = 'userStories';
          break;
        case 'Final PRD':
          generatorFn = generateFinalPRD;
          documentType = 'finalPRD';
          break;
        default:
          throw new Error(`No generator available for section: ${sectionName}`);
      }
      
      if (!generatorFn) {
        throw new Error(`Generator not available for ${sectionName}`);
      }
      
      const document = await generatorFn(projectId);
      
      // Update state with generated content
      setEditContent(document.content);
      
      // Save content via parent
      if (onSave) {
        onSave(document.content);
      }
      
      // Also save as markdown file
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `${sectionName.toLowerCase().replace(/\s+/g, '-')}-generated-${timestamp}.md`;
      
      try {
        const savedFile = await saveMdFile(projectId, filename, document.content);
        console.log('Saved generated content as markdown file:', savedFile);
      } catch (mdError) {
        console.error('Error saving markdown file:', mdError);
      }
      
      // Mark document as saved
      setIsDocumentSaved(true);
      
      showNotification?.({
        message: `${sectionName} content generated and saved successfully`,
        type: 'success'
      });
      
      // Switch to preview mode
      setEditMode(false);
    } catch (error) {
      console.error(`Error generating ${sectionName}:`, error);
      showNotification?.({
        message: `Error generating ${sectionName}: ${error.message}`,
        type: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle save action
  const handleSave = async () => {
    if (!editContent || editContent.trim() === '') {
      console.log('No content to save, aborting save operation');
      return;
    }
    
    try {
      // Save to parent component
      if (onSave) {
        onSave(editContent);
      }
      
      // Create document in backend
      if (projectId) {
        // Map section names to valid document types
        let documentType;
        switch (sectionName) {
          case 'Field Validations':
            documentType = 'validation';
            break;
          case 'Technology Stack':
            documentType = 'tech_stack';
            break;
          case 'Development Guidelines':
            documentType = 'dev_guidelines';
            break;
          case 'Design Guidelines':
            documentType = 'design_guidelines';
            break;
          case 'Security Guidelines':
            documentType = 'security_guidelines';
            break;
          case 'API Guidelines':
            documentType = 'api_guidelines';
            break;
          case 'Features':
            documentType = 'features';
            break;
          case 'User Stories':
            documentType = 'user_stories';
            break;
          case 'Final PRD':
            documentType = 'final_prd';
            break;
          default:
            documentType = sectionName.toLowerCase().replace(/\s+/g, '_');
        }
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const documentTitle = `${sectionName} for Project ${projectId}`;
        
        // First create document record in database
        const newDocument = await createDocument(projectId, {
          title: documentTitle,
          content: editContent,
          type: documentType
        });
        
        console.log('Created document:', newDocument);
        
        // Also save as markdown file
        const filename = `${sectionName.toLowerCase().replace(/\s+/g, '-')}-${timestamp}.md`;
        const savedFile = await saveMdFile(projectId, filename, editContent);
        
        console.log('Saved markdown file:', savedFile);
        
        // Mark document as saved
        setIsDocumentSaved(true);
        
        showNotification?.({
          message: `${sectionName} document saved to backend and as .md file`,
          type: 'success'
        });
      }
      
      // Exit edit mode
      setEditMode(false);
    } catch (error) {
      console.error(`Error saving ${sectionName}:`, error);
      showNotification?.({
        message: `Warning: Content updated but couldn't save document to backend: ${error.message}`,
        type: 'warning'
      });
    }
  };

  // Handle content from chat
  const handleChatContent = async (chatContent) => {
    if (!chatContent) return;
    
    try {
      // Update local content
      setEditContent(chatContent);
      
      // Save to parent component
      if (onSave) {
        onSave(chatContent);
      }
      
      // Create document in backend
      if (projectId) {
        // Map section names to valid document types
        let documentType;
        switch (sectionName) {
          case 'Field Validations':
            documentType = 'validation';
            break;
          case 'Technology Stack':
            documentType = 'tech_stack';
            break;
          case 'Development Guidelines':
            documentType = 'dev_guidelines';
            break;
          case 'Design Guidelines':
            documentType = 'design_guidelines';
            break;
          case 'Security Guidelines':
            documentType = 'security_guidelines';
            break;
          case 'API Guidelines':
            documentType = 'api_guidelines';
            break;
          case 'Features':
            documentType = 'features';
            break;
          case 'User Stories':
            documentType = 'user_stories';
            break;
          case 'Final PRD':
            documentType = 'final_prd';
            break;
          default:
            documentType = sectionName.toLowerCase().replace(/\s+/g, '_');
        }
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const documentTitle = `${sectionName} from Chat for Project ${projectId}`;
        
        // First create document record in database
        const newDocument = await createDocument(projectId, {
          title: documentTitle,
          content: chatContent,
          type: documentType
        });
        
        console.log('Created document from chat:', newDocument);
        
        // Also save as markdown file
        const filename = `${sectionName.toLowerCase().replace(/\s+/g, '-')}-chat-${timestamp}.md`;
        const savedFile = await saveMdFile(projectId, filename, chatContent);
        
        console.log('Saved markdown file from chat:', savedFile);
        
        // Mark document as saved
        setIsDocumentSaved(true);
        
        showNotification?.({
          message: `${sectionName} document saved from chat content`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error(`Error saving ${sectionName} from chat:`, error);
      showNotification?.({
        message: `Warning: Content updated but couldn't save document to backend: ${error.message}`,
        type: 'warning'
      });
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {useGrootMode && (
            <GrootAvatar 
              src={grootImageUrl} 
              sx={{ mr: 1, width: 30, height: 30 }} 
            />
          )}
          <Typography variant="subtitle1">
            {title || sectionName}
            {content && (
              <Chip 
                label={isDocumentSaved ? "Document Saved" : "Content Added"} 
                color={isDocumentSaved ? "success" : "primary"} 
                size="small" 
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Auto Generate button */}
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleGenerateContent}
            startIcon={isGenerating ? <CircularProgress size={18} /> : <RefreshIcon />}
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : generateLabel}
          </Button>
          
          {/* Edit button */}
          {!editMode && content && (
            <Button 
              variant="outlined" 
              onClick={() => setEditMode(true)}
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
          )}
        </Box>
      </Box>
      
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab 
          icon={editMode ? <CodeIcon /> : <PreviewIcon />} 
          label={editMode ? "Edit" : "Preview"} 
        />
        <Tab icon={<PreviewIcon />} label="Chat" />
      </Tabs>
      
      {selectedTab === 0 && (
        <>
          {editMode ? (
            <Box mt={2}>
              <CodeMirror
                value={editContent}
                height="400px"
                onChange={(value) => setEditContent(value)}
                extensions={[markdown()]}
                placeholder={placeholder}
              />
              <Box mt={2} display="flex" justifyContent="flex-end">
                {content && (
                  <Button 
                    variant="outlined" 
                    onClick={() => setEditMode(false)}
                    sx={{ mr: 1 }}
                  >
                    Cancel
                  </Button>
                )}
                <Button 
                  variant="contained" 
                  onClick={handleSave}
                  startIcon={<SaveIcon />}
                  disabled={!editContent || !editContent.trim()}
                >
                  Save
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ maxHeight: '400px', overflow: 'auto', mt: 2 }}>
                {isGenerating ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ mt: 2 }}>
                      Generating {sectionName}...
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      This may take a moment as we're creating high-quality content with real-time AI streaming.
                    </Typography>
                  </Box>
                ) : (content !== null && content !== undefined && content.trim() !== '') ? (
                  <>
                    <ReactMarkdown>
                      {content}
                    </ReactMarkdown>
                    {isDocumentSaved && (
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Chip 
                          icon={<FileDownloadIcon />}
                          label="Document saved to project" 
                          color="success" 
                          variant="outlined"
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 1, textAlign: 'center' }}>
                    <Typography color="text.secondary" variant="body1" gutterBottom>
                      No content has been added for {sectionName} yet.
                    </Typography>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      Click "{generateLabel}" to auto-generate content with AI.
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      Or edit directly by clicking "Edit" or switch to the "Chat" tab for interactive assistance.
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </>
      )}
      
      {selectedTab === 1 && (
        <Box mt={2}>
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center" 
            mb={2}
            p={1}
            borderRadius={1}
            bgcolor={useGrootMode ? "rgba(46, 125, 50, 0.08)" : "transparent"}
            border={1}
            borderColor={useGrootMode ? "rgba(46, 125, 50, 0.3)" : "transparent"}
          >
            <Box display="flex" alignItems="center">
              {useGrootMode && (
                <GrootAvatar 
                  src={grootImageUrl} 
                  sx={{ mr: 1, width: 24, height: 24 }} 
                />
              )}
              <Typography variant="subtitle2" color={useGrootMode ? "success.dark" : "text.secondary"}>
                {useGrootMode ? "Groot is ready to help you!" : "Standard AI assistant mode"}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch 
                  checked={useGrootMode}
                  onChange={() => setUseGrootMode(!useGrootMode)}
                  color="success"
                />
              }
              label={useGrootMode ? "Groot Mode" : "Standard Mode"}
            />
          </Box>
          
          {useGrootMode ? (
            <GrootWorkflowChat
              sectionName={sectionName}
              projectId={projectId}
              existingContent={content}
              onSaveContent={handleChatContent}
              placeholder={`Ask Groot about ${sectionName.toLowerCase()}...`}
              relatedSections={relatedSections}
            />
          ) : (
            <WorkflowChat
              sectionName={sectionName}
              projectId={projectId}
              existingContent={content}
              onSaveContent={handleChatContent}
              placeholder={`Ask AI to help with ${sectionName.toLowerCase()}...`}
              relatedSections={relatedSections}
            />
          )}
        </Box>
      )}
    </Paper>
  );
};

export default ContentEditor;